import type { Ref } from 'react';
import { createContext, useContext } from 'react';
import type { ButtonProps as AriaButtonProps } from 'react-aria-components/Button';
import { Button as AriaButton } from 'react-aria-components/Button';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { ContextValue } from 'react-aria-components/slots';
import { ClearPressResponder } from '@react-aria/interactions';
import type { AriaLabelingProps } from '@react-types/shared';
import type { VariantProps } from 'class-variance-authority';
import { cva, cx } from 'class-variance-authority';

import type { IconProps } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import type { ButtonVariants } from './Button';
import { buttonStyles } from './Button';
import { disabledTooltipTriggerProps, PerceivableContext, useIsDisabledTooltipTrigger } from './Perceivable';
import { useLPContextProps } from './utils';

import styles from './styles/IconButton.module.css';

const iconButtonStyles = cva(styles.base, {
	variants: {
		size: {
			small: styles.small,
			medium: styles.medium,
		},
	},
	defaultVariants: {
		size: 'medium',
	},
});

interface IconButtonVariants extends VariantProps<typeof iconButtonStyles> {
	variant?: Extract<ButtonVariants['variant'], 'default' | 'primary' | 'destructive' | 'minimal'>;
}

interface IconButtonBaseProps extends Required<Pick<AriaLabelingProps, 'aria-label'>>, IconButtonVariants {
	icon: IconProps['name'];
	children?: never;
}
interface IconButtonProps extends Omit<AriaButtonProps, 'children' | 'aria-label'>, IconButtonBaseProps {
	ref?: Ref<HTMLButtonElement>;
}

interface IconButtonContextValue extends IconButtonProps {
	isPressed?: boolean;
}

const IconButtonContext = createContext<ContextValue<IconButtonContextValue, HTMLButtonElement>>(null);

/**
 * A button allows a user to perform an action, with mouse, touch, and keyboard interactions.
 *
 * https://react-spectrum.adobe.com/react-aria/Button.html
 */
const IconButton = ({ ref, ...props }: IconButtonProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, IconButtonContext);
	const perceivableProps = useContext(PerceivableContext);
	const isDisabledTrigger = useIsDisabledTooltipTrigger(mergedProps.isDisabled);
	const { size = 'medium', variant = 'default', icon } = mergedProps;

	const button = (
		<AriaButton
			{...mergedProps}
			{...perceivableProps}
			{...(isDisabledTrigger && disabledTooltipTriggerProps)}
			ref={mergedRef}
			data-lp-variant={variant}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				cx(buttonStyles({ ...renderProps, size, variant, className }), iconButtonStyles({ size })),
			)}
		>
			<Icon name={icon} size="small" aria-hidden />
		</AriaButton>
	);

	return isDisabledTrigger ? <ClearPressResponder>{button}</ClearPressResponder> : button;
};

export { IconButton, IconButtonContext, iconButtonStyles };
export type { IconButtonProps, IconButtonBaseProps };
